import React, { useEffect, useState } from 'react';
import _get from 'lodash/get'
import _shuffle from 'lodash/shuffle';  
import _slice from 'lodash/slice';
import _isEmpty from 'lodash/isEmpty';
import propTypes from 'prop-types';
import { TextContent } from '_ui-core-js/React/components/TextContent.jsx';
import { Button } from '_ui-core-js/React/components/Button.jsx';
import { ImageSelectionGrid } from '_ui-core-js/React/components/ImageSelectionGrid.jsx';
import { StepProgressTracker } from '_ui-core-js/React/components/StepProgessTracker.jsx';
import { ErrorMessage } from '_ui-core-js/React/components/ErrorMessage.jsx';
import { VIEWS } from './AiExperience.jsx';
import { fireGTMTrackingEvent } from '_ui-core-js/utilities//fireGTMTrackingEvent.js';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';
import { LBX_GTM_TRACKING_VALUES } from '../../constants/analytics.js';

export function ColorPaletteSelectionScreen(props) {

  const { block, data, state } = props;
  const { title, subtitle, copy, ctas, progressTrackerLabel, progressTrackerPaginationDivider } = data;
  const { enableInstagramFlow } = _get(state, 'settings', {});
  const imageSelectionMax = 4;
  const initSelected = state.selectedColorPaletteImages?.length ? state.selectedColorPaletteImages.map(image => image.index) : undefined;
  const gridImages = (state.loggedIn && state.instagramCode) ? state.instagramImageFeed : state.curatedColorPaletteGridImages;
  const [selectedImages, setSelectedImages] = useState(state.selectedColorPaletteImages);
  const [selectionsComplete, setSelectionsComplete] = useState(selectedImages.length === imageSelectionMax);

  useEffect(() => {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.workflowEvent,
      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: CORE_GTM_TRACKING_VALUES.action.workflowStep,
      label: CORE_GTM_TRACKING_VALUES.label.imageSelectionScreen
    }, {
      workflow: {
        name: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience,
        step: CORE_GTM_TRACKING_VALUES.label.imageSelectionScreen,
        conversion: 0,
        variant: (state.loggedIn && state.instagramCode) ? 'instagram' : 'not-connected'
      }
    });
  }, []);

  function updateImageSelection(selectedImagesArray) {
    if (selectedImagesArray.length > selectedImages.length) {
      fireGTMTrackingEvent({
        name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
        componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
        componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
        action: CORE_GTM_TRACKING_VALUES.action.addImage,
        label: ''
      });
    }

    setSelectedImages(selectedImagesArray.slice(0));
    setSelectionsComplete(selectedImagesArray.length === imageSelectionMax);
  };

  function proceedToColorPaletteGeneration() {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: CORE_GTM_TRACKING_VALUES.action.nextStep,
      label: CORE_GTM_TRACKING_VALUES.label.imageSelectionScreen
    });

    state.setSelectedColorPaletteImages(selectedImages);
    state.setColorPaletteHexValuesSelectedIndexes([0, 0, 0, 0]); // pick first color in array for each image color palette
    state.setActiveView(VIEWS.colorPaletteGeneration.name);
  };

  function goBackToLogin() {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: CORE_GTM_TRACKING_VALUES.action.previousStep,
      label: CORE_GTM_TRACKING_VALUES.label.imageSelectionScreen
    });

    state.setSelectedColorPaletteImages(selectedImages);
    state.setActiveView(VIEWS.login.name);
  };

  return (
    <div className={`${block}__color-palette-selection-screen ${block}__layout--horizontal container-xxxl`}>
      <div className='row'>
        <ErrorMessage 
        message={state.error} 
        displayCondition={!!state.error}
        cssAnimationDelay={1} 
        />
        <div className={`${block}__layout--block-one col-12 col-md-5 col-lg-4`}>
          <TextContent 
          heading={title}
          subheading={subtitle}
          body={copy}
          modifier='centered-mobile'
          cssAnimationTrigger={true}
          />    
          <StepProgressTracker 
          steps={imageSelectionMax}
          currentStep={selectedImages.length}
          label={progressTrackerLabel}
          showPagination={!!progressTrackerLabel}
          paginationDivider={progressTrackerPaginationDivider}
          layoutDirection='horizontal'
          cssAnimationDelay={0.5}
          />
          <div className={`${block}__cta-container`}>
            <Button 
            label={ctas.next.label} 
            type='primary' 
            cssAnimation={true} 
            cssAnimationName='vertical-top-bottom-clip'
            cssAnimationTrigger={true}
            cssAnimationDuration={0.5}
            cssAnimationDelay={1} 
            disabled={!selectionsComplete}
            onClickCallback={proceedToColorPaletteGeneration}
            />
            { enableInstagramFlow &&
              <Button 
              label={ctas.back.label}
              type='tertiary' 
              icon='back'
              slim={true}
              cssAnimation={true} 
              cssAnimationTrigger={true}
              cssAnimationDelay={1.5}
              onClickCallback={goBackToLogin}
              /> 
            }
          </div>
        </div>
        <div className={`${block}__layout--block-two col-12 col-md-6 offset-md-1 offset-lg-2`}>
          <ImageSelectionGrid 
          images={gridImages} 
          loading='eager'
          initSelected={initSelected} // if has selected 
          onSelectCallback={selectedImagesArray => updateImageSelection(selectedImagesArray)}
          maxSelection={imageSelectionMax}
          />
        </div>
      </div>
    </div>
  )
};

ColorPaletteSelectionScreen.propTypes = {
  block: propTypes.string.isRequired,
  state: propTypes.object.isRequired,
  data: propTypes.object.isRequired
};